import { useMagicLinkStore } from '@/stores/magic-link'
import { useUserStore } from '@/stores/user'
import type { LocationQuery, RouteLocation, RouteLocationRaw } from 'vue-router'
import { useToast } from 'vue-toastification'

let isProcessingLink = false
const SEEN_TOKEN_KEY = 'magic_link_seen_token'

function getSessionStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage
    }

    if ('sessionStorage' in globalThis) {
      const storage =
        (globalThis as { sessionStorage?: Storage }).sessionStorage ?? null
      return storage ?? null
    }
  } catch (error) {
    console.warn('Unable to access sessionStorage for magic-link guard', error)
  }

  return null
}

function readSeenToken(storage: Storage | null): string | null {
  try {
    return storage?.getItem(SEEN_TOKEN_KEY) ?? null
  } catch (error) {
    console.warn('Unable to read sessionStorage for magic-link guard', error)
    return null
  }
}

function writeSeenToken(storage: Storage | null, token: string): void {
  try {
    storage?.setItem(SEEN_TOKEN_KEY, token)
  } catch (error) {
    console.warn('Unable to persist magic-link state', error)
  }
}

function toStringQueryParam(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value
  }

  if (Array.isArray(value)) {
    return value[0]
  }

  return undefined
}

function decodeAuthLink(encodedLink: string): string {
  if (typeof globalThis.atob !== 'function') {
    throw new Error('Base64 decoder not available')
  }

  return globalThis.atob(encodedLink)
}

function removeMagicLinkQuery(to: RouteLocation): {
  cleanedQuery: LocationQuery
  redirect: string | undefined
} {
  const cleanedQuery: LocationQuery = { ...to.query }
  delete cleanedQuery.alink

  const redirectParam = toStringQueryParam(to.query.redirect)
  const redirect =
    redirectParam && redirectParam.startsWith('/') ? redirectParam : undefined

  return { cleanedQuery, redirect }
}

function buildNavigationResult(
  to: RouteLocation,
  query: LocationQuery
): RouteLocationRaw {
  const params = to.params ? { ...to.params } : undefined

  return {
    path: to.path,
    hash: to.hash,
    query,
    ...(params ? { params } : {}),
    replace: true,
  }
}

export default async function magicLinkAuthGuard(to: RouteLocation) {
  const authLinkParam = toStringQueryParam(to.query.alink)

  if (!authLinkParam) {
    return true
  }

  const toast = useToast()
  const userStore = useUserStore()
  const magicLinkStore = useMagicLinkStore()
  const { cleanedQuery, redirect } = removeMagicLinkQuery(to)
  const storage = getSessionStorage()
  const lastSeenToken = readSeenToken(storage)

  if (lastSeenToken === authLinkParam) {
    return buildNavigationResult(to, cleanedQuery)
  }

  const shouldForceSwitch = userStore.isLoggedIn

  if (isProcessingLink) {
    return buildNavigationResult(to, cleanedQuery)
  }

  try {
    isProcessingLink = true
    magicLinkStore.startProcessing()
    const decodedAuthLink = decodeAuthLink(authLinkParam)
    if (shouldForceSwitch) {
      await userStore.magicLinkLogin(decodedAuthLink, { force: true })
    } else {
      await userStore.magicLinkLogin(decodedAuthLink)
    }
    writeSeenToken(storage, authLinkParam)
    toast.success('Successfully signed in!', {
      id: 'magic-link-success',
    })

    if (redirect) {
      return { path: redirect, replace: true }
    }

    return buildNavigationResult(to, cleanedQuery)
  } catch (error) {
    console.error('Magic link authentication failed:', error)
    toast.error('Invalid or expired magic link', {
      id: 'magic-link-error',
    })
    writeSeenToken(storage, authLinkParam)
    return buildNavigationResult(to, cleanedQuery)
  } finally {
    isProcessingLink = false
    magicLinkStore.stopProcessing()
  }
}
